import { yupResolver } from '@hookform/resolvers/yup';
import { useUTM } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { Alert, semanticColor } from '@repo/ui';
import { Button, Control, Fieldset, TextField, Title } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { pascalCase } from 'es-toolkit';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { CertType } from '@/__generated__/simpleAuthRequestTokenMutation.graphql';
import { SubmitHandler } from '@/components/tax-refund/simple-auth/input/SimpleAuthInputContainer';
import s from '@/components/tax-refund/simple-auth/input/SimpleAuthInputForm.module.scss';
import { useSimpleAuthUserInfo } from '@/lib/hooks/use-hometax-simple-auth';
import { useFromTypeStaticData } from '@/lib/hooks/use-simple-auth-from-type';
import { simpleAuthFormSchema } from '@/lib/regex/schema/simple-auth';
import { simpleAuthCertTypeAtom, simpleAuthFromTypeAtom } from '@/lib/stores/simple-auth';
import { SimpleAuthCertificateType } from '@/lib/types/simple-auth';

const simpleAuthButtons: { snsType: SimpleAuthCertificateType; name: string; imagePath: string }[] = [
  {
    snsType: 'kakao',
    name: '카카오',
    imagePath: '/refund/images/simple-auth/cert-kakao-app.svg'
  },
  {
    snsType: 'naver',
    name: '네이버',
    imagePath: '/refund/images/simple-auth/cert-naver-app.svg'
  }
];

export const SimpleAuthInputForm = ({ onSubmit, isLoading }: { onSubmit: SubmitHandler; isLoading: boolean }) => {
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, isSubmitted },
    clearErrors
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(simpleAuthFormSchema)
  });
  const { sendClickEvent } = useUserEventLogger();
  const simpleAuthFromType = useAtomValue(simpleAuthFromTypeAtom);
  const { utmData } = useUTM();
  const { getInputFormClickEvent, getCertTypeClickEvent } = useFromTypeStaticData(simpleAuthFromType);
  const prefillData = useSimpleAuthUserInfo();
  const savedCertType = useAtomValue(simpleAuthCertTypeAtom);
  const isEnterNaver = useMemo(() => !!utmData?.utmCampaign?.includes('naver'), [utmData?.utmCampaign]);
  const sortedCertTypes = useMemo(() => (isEnterNaver ? simpleAuthButtons.slice().reverse() : simpleAuthButtons), [isEnterNaver]);
  const isInit = useRef(false);

  const formClickEvents = getInputFormClickEvent();
  const initializeForm = useCallback(() => {
    isInit.current = true;
    const { name, phone, birthday } = prefillData ?? {};
    if (name || phone || birthday || savedCertType) {
      reset({
        name: name ?? '',
        phone: formatPhoneNumber(phone ?? ''),
        birthDay: formatBirthDate(birthday ?? ''),
        certType: savedCertType ?? (isEnterNaver ? 'naver' : 'kakao')
      });
    }
  }, [prefillData, reset, savedCertType, isEnterNaver]);

  useEffect(() => {
    if (isInit.current) return;
    initializeForm();
  }, [initializeForm]);

  return (
    <div className={'page-padding'}>
      <Title.Headline title={'간편인증을 위해\n정보를 입력해주세요'} size={'large'} />
      <Alert
        variant={'custom'}
        size={'small'}
        className={'mt-tiny-large mb-normal-medium'}
        graphic={
          <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/iconography/icg_shield-person_md.svg`} className={'w-5 h-5'} alt="" aria-hidden />
        }
        bg={semanticColor.neutral.background.lowest}
      >
        정보는 국가 공인 ISMS 기준에 따라 안전하게 보호됩니다.
      </Alert>
      <div className={'flex flex-col gap-3 pb-[108px]'}>
        <Fieldset.Wrapper isInvalid={!!errors.name}>
          <Fieldset.Label>이름</Fieldset.Label>
          <TextField.Basic
            {...register('name', {
              onChange: (e) => {
                if (errors.name) {
                  clearErrors('name');
                }
                setValue('name', e.target.value.trim());
              }
            })}
            maxLength={50}
            type={'text'}
            className={'w-full'}
            error={isSubmitted && !!errors.name}
            onFocus={() => {
              sendClickEvent(formClickEvents.name);
            }}
          />
          {isSubmitted && errors.name && <Fieldset.Error>{errors?.name?.message as string}</Fieldset.Error>}
        </Fieldset.Wrapper>
        <Fieldset.Wrapper isInvalid={!!errors.birthDay} className="w-full">
          <Fieldset.Label>생년월일</Fieldset.Label>
          <TextField.Basic
            {...register('birthDay', {
              required: true,
              onChange: (e) => {
                if (errors.birthDay) {
                  clearErrors('birthDay');
                }
                setValue('birthDay', formatBirthDate(e.target.value));
              }
            })}
            maxLength={20}
            type={'text'}
            className={'w-full'}
            pattern="\d*"
            inputMode={'numeric'}
            placeholder={'YYYY.MM.DD'}
            error={isSubmitted && !!errors.birthDay}
            onFocus={() => {
              sendClickEvent(formClickEvents.birthday);
            }}
          />
          {isSubmitted && errors.birthDay && <Fieldset.Error>{errors?.birthDay?.message as string}</Fieldset.Error>}
        </Fieldset.Wrapper>
        <Fieldset.Wrapper isInvalid={!!errors.phone} className="w-full">
          <Fieldset.Label>휴대폰 번호</Fieldset.Label>
          <TextField.Basic
            {...register('phone', {
              onChange: (e) => {
                if (errors.phone) {
                  clearErrors('phone');
                }
                setValue('phone', formatPhoneNumber(e.target.value));
              }
            })}
            minLength={13}
            maxLength={20}
            type={'text'}
            className={'w-full'}
            pattern="\d*"
            inputMode={'numeric'}
            placeholder={'010-0000-0000'}
            error={isSubmitted && !!errors.phone}
            onFocus={() => {
              sendClickEvent(formClickEvents.phone);
            }}
          />
          {isSubmitted && errors.phone && <Fieldset.Error>{errors?.phone?.message as string}</Fieldset.Error>}
        </Fieldset.Wrapper>
        <Fieldset.Wrapper isInvalid={!!errors.certType} className="w-full">
          <Fieldset.Label>본인인증 수단</Fieldset.Label>
          <div className={'mt-[4px] flex justify-between gap-4 pb-[55px]'}>
            {sortedCertTypes.map(({ snsType, imagePath, name }) => (
              <Controller
                key={`${snsType}Auth`}
                name={'certType'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <button
                      id={snsType}
                      className={`${cn({ [`${s.selected}`]: value === snsType }, s.sns_button)} relative`}
                      onClick={(e) => {
                        const certType = e.currentTarget.id as SimpleAuthCertificateType;
                        sendClickEvent(getCertTypeClickEvent(certType));
                        onChange(certType);
                      }}
                    >
                      <div className={'pointer-events-none absolute top-2 left-2'}>
                        <Control.CheckBox label="" defaultChecked={value === snsType} />
                      </div>
                      <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${imagePath}`} width={48} height={48} alt={name} />
                      <p>{`${name}로 인증하기`}</p>
                    </button>
                  </>
                )}
              />
            ))}
          </div>
          <Fieldset.Error>{errors?.certType?.message as string}</Fieldset.Error>
        </Fieldset.Wrapper>
      </div>

      <Button.StickyBottom.Wrapper className={'page-padding'}>
        <Button.StickyBottom.Button
          disabled={isSubmitted && Object.values(errors).some((error) => !!error)}
          state={'primary'}
          loading={isLoading}
          onClick={handleSubmit(() => {
            const { name, phone, birthDay, certType } = getValues();
            onSubmit(pascalCase(certType) as CertType, {
              name,
              phone: phone?.replace(/-/g, ''),
              birthday: birthDay?.replace(/\./g, '')
            });
          })}
        >
          다음
        </Button.StickyBottom.Button>
      </Button.StickyBottom.Wrapper>
    </div>
  );
};

const formatPhoneNumber = (value: string) => {
  return value
    .trim()
    .replace(/-/g, '')
    .replace(/^(\d{3})(\d{3,4})(\d{4})$/, `$1-$2-$3`);
};

const formatBirthDate = (value: string) => {
  return value
    .trim()
    .replace(/\./g, '')
    .replace(/^(\d{4})(\d{2})(\d{2})$/, `$1.$2.$3`);
};
